/**
 * Utility functions for text direction detection and RTL support
 */

/**
 * Detects if text contains Arabic characters
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains Arabic characters
 */
export const isArabicText = (text) => {
  if (!text || text.trim() === '') return false
  
  // Arabic Unicode ranges:
  // U+0600-U+06FF: Arabic
  // U+0750-U+077F: Arabic Supplement
  // U+08A0-U+08FF: Arabic Extended-A
  // U+FB50-U+FDFF: Arabic Presentation Forms-A
  // U+FE70-U+FEFF: Arabic Presentation Forms-B
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/
  
  return arabicRegex.test(text)
}

/**
 * Gets the text direction based on content
 * @param {string} text - The text to analyze
 * @returns {string} - 'rtl' for Arabic text, 'ltr' for other text
 */
export const getTextDirection = (text) => {
  return isArabicText(text) ? 'rtl' : 'ltr'
}

/**
 * Gets the text alignment based on content
 * @param {string} text - The text to analyze
 * @returns {string} - 'right' for Arabic text, 'left' for other text
 */
export const getTextAlign = (text) => {
  return isArabicText(text) ? 'right' : 'left'
}

/**
 * Gets RTL styles for input elements
 * @param {string} text - The text content
 * @returns {object} - Style object with direction and textAlign
 */
export const getRTLStyles = (text) => {
  const isArabic = isArabicText(text)
  return {
    direction: isArabic ? 'rtl' : 'ltr',
    textAlign: isArabic ? 'right' : 'left'
  }
}
